import React, { useState } from "react";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, FileDown, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

export default function AdminBulkUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.created + data.updated} rows`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/products/bulk-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "products-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Template download failed");
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
        <Upload className="w-6 h-6 text-hyd-gold" /> Bulk product upload
      </h1>
      <p className="text-hyd-text2 text-sm">Upload a .csv file to create or update multiple products at once. Existing products (by slug) will be updated in place.</p>

      <div className="mt-5 grid md:grid-cols-2 gap-5">
        <div className="bg-hyd-panel border border-hyd-border rounded-xl p-5">
          <h3 className="font-display text-lg font-bold">1. Get the template</h3>
          <p className="text-sm text-hyd-text2 mt-1">Download the CSV template with all the required columns and one sample row.</p>
          <Button data-testid={ADMIN.bulkTemplateBtn} onClick={downloadTemplate} variant="secondary" className="mt-3 bg-hyd-panel2 border border-hyd-border text-hyd-gold">
            <FileDown className="w-4 h-4 mr-1" /> Download template CSV
          </Button>
          <div className="mt-4 text-xs text-hyd-text2">
            <div className="font-bold text-hyd-text mb-1">Required columns:</div>
            <code className="text-[11px] block bg-hyd-bg p-2 rounded border border-hyd-border">name, slug, category_slug, price</code>
            <div className="font-bold text-hyd-text mt-2 mb-1">Optional columns:</div>
            <code className="text-[11px] block bg-hyd-bg p-2 rounded border border-hyd-border">
              brand, description, mrp, stock, unit, image_url, is_green_cracker, green_cert_number, peso_license, tags, is_bestseller, is_new, active, seo_title, seo_description
            </code>
          </div>
        </div>

        <div className="bg-hyd-panel border border-hyd-border rounded-xl p-5">
          <h3 className="font-display text-lg font-bold">2. Upload your CSV</h3>
          <div className="mt-3 border-2 border-dashed border-hyd-border rounded-lg p-6 text-center">
            <input
              data-testid={ADMIN.bulkFileInput}
              id="bulk-file"
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
            />
            <label htmlFor="bulk-file" className="cursor-pointer inline-block">
              <Upload className="w-8 h-8 mx-auto text-hyd-gold" />
              <div className="mt-2 text-sm">{file ? file.name : "Click to choose a .csv file"}</div>
              <div className="text-xs text-hyd-text3 mt-1">Max ~1MB, UTF-8 encoded</div>
            </label>
          </div>
          <Button
            data-testid={ADMIN.bulkUploadBtn}
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full mt-3 bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold"
          >
            {uploading ? "Uploading..." : "Upload & import"}
          </Button>
          {result && (
            <div className="mt-4 space-y-2">
              <div className="p-3 rounded-lg bg-emerald-950/40 border border-emerald-800 text-emerald-200 text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Created {result.created}, updated {result.updated}
              </div>
              {result.errors?.length > 0 && (
                <div className="p-3 rounded-lg bg-red-950/40 border border-red-800 text-red-200 text-xs">
                  <div className="flex items-center gap-2 font-bold mb-1"><AlertTriangle className="w-4 h-4" /> {result.errors.length} errors</div>
                  <ul className="space-y-0.5 max-h-40 overflow-auto">
                    {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
